import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Table, TableContainer, Text, Button, Tooltip } from '@electricjs/arc';
import { PermissionEnum } from '@electricjs/core_entity-client';

import { SiteWithAddress } from '@/types/assets';
import { createColumnHelper } from '@tanstack/react-table';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { formatInternationalPhoneNumber } from 'common/utils/formatPhoneNumber';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';

type SitesTableProps = {
  data: SiteWithAddress[];
  isSitesLoading: boolean;
  onArchiveClick?: (site: SiteWithAddress) => void;
  maxHeight?: string;
};

const columnHelper = createColumnHelper<SiteWithAddress>();

const SitesTable = ({
  data,
  isSitesLoading,
  onArchiveClick,
  maxHeight = '30rem',
}: SitesTableProps) => {
  const { t } = useTranslation('assets');
  const { siteArchiveEnabled } = useFlags();
  const canDeleteSites = useUserHasOrganizationPermission(
    PermissionEnum.Sitesdelete
  );

  const showArchiveColumn = siteArchiveEnabled && canDeleteSites;

  const tableColumns = [
    columnHelper.accessor('name', {
      header: t('sitesTable.name'),
      id: 'name',
      meta: {
        minWidth: 30,
      },
      cell: ({ getValue }) => {
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{getValue()}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('address', {
      header: t('sitesTable.address'),
      id: 'address',
      meta: {
        minWidth: 80,
        flex: '2',
      },
      cell: ({ getValue }) => {
        const address = getValue();
        const addressText = `${address.streetAddress1} ${address.streetAddress2}, ${address.city}, ${address.state}, ${address.zip}, ${address.country}`;
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{addressText}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    columnHelper.accessor('phone', {
      header: t('sitesTable.phone'),
      id: 'phone',
      meta: {
        minWidth: 20,
        flex: '1.5',
      },
      cell: ({ getValue }) => {
        const formattedPhone = formatInternationalPhoneNumber(getValue());
        return (
          <TableCellFlexContainer>
            <Text variant="body-2">{formattedPhone}</Text>
          </TableCellFlexContainer>
        );
      },
    }),
    ...(showArchiveColumn
      ? [
          columnHelper.display({
            id: 'archive',
            header: '',
            meta: {
              minWidth: 20,
            },
            cell: ({ row }) => {
              const site = row.original;
              const archiveButton = (
                <Button
                  id={`archive-site-${site.id}`}
                  variant="text"
                  intent="danger"
                  size="small"
                  disabled={!site.isArchivable}
                  onClick={() => onArchiveClick?.(site)}>
                  {t('sitesTable.archive')}
                </Button>
              );

              if (!site.isArchivable) {
                return (
                  <TableCellFlexContainer>
                    <Tooltip
                      content={t('sitesTable.archiveDisabledTooltip')}
                      placement="top">
                      <span>{archiveButton}</span>
                    </Tooltip>
                  </TableCellFlexContainer>
                );
              }

              return (
                <TableCellFlexContainer>{archiveButton}</TableCellFlexContainer>
              );
            },
          }),
        ]
      : []),
  ];

  return (
    <TableContainer
      vertical="top"
      hAlignContent="center"
      width="100%"
      alignSelf="flex-start"
      flexDirection="column"
      flexWrap="nowrap"
      maxHeight={maxHeight}>
      <Table
        id="sites-table"
        columns={tableColumns}
        data={data}
        loading={isSitesLoading}
        emptyStateText={t('sitesTable.emptyState')}
      />
    </TableContainer>
  );
};

export default SitesTable;
